import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { X, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

export default function TransferModal({ entityType, entityId, currentOwnerId, users, onClose, onDone }) {
  const [newOwner, setNewOwner] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const candidates = users.filter((u) => u.active !== false && u.id !== currentOwnerId);
  const selectedUser = candidates.find((u) => u.id === newOwner);

  const submit = async (e) => {
    e.preventDefault();
    if (!newOwner) return;
    setSaving(true);
    try {
      await api.post(`/${entityType === "lead" ? "leads" : "opportunities"}/${entityId}/transfer`, {
        assigned_to: newOwner,
        notify,
      });
      toast.success(notify ? `Transferred · ${selectedUser?.name} notified by email` : "Ownership transferred");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="transfer-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-[#29ABE2]" />
            <h3 className="font-heading text-lg font-semibold">Transfer ownership</h3>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <span className="eyb-label">New owner</span>
            <select
              data-testid="transfer-owner-select"
              required
              value={newOwner}
              onChange={(e) => setNewOwner(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
            >
              <option value="">— select a user —</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role.replace("_", " ")}</option>
              ))}
            </select>
          </label>

          <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <input
              data-testid="transfer-notify-checkbox"
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#29ABE2]"
            />
            <div>
              <div className="text-sm font-semibold text-slate-800">Notify the new owner by email</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {selectedUser ? <>Sends a transfer notification to <span className="font-medium">{selectedUser.email}</span></> : "An email is sent to the chosen owner when checked"}
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-transfer" type="submit" disabled={saving || !newOwner} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] disabled:opacity-50 text-white text-sm font-semibold rounded-full">
              {saving ? "Transferring…" : "Transfer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
